import { memoize } from '@exodus/basic-utils';
import EventEmitter from 'eventemitter3';
import createFetchival from '@exodus/fetch/create-fetchival';
import { SearchDirection } from './types.js';
import getFiatBalance from '../utils/get-fiat-balance.js';
import AssetAdapter from '../adapters/asset.js';
const MEMOIZATION_TIMEOUT = 1000 * 60 * 5;
export class ExchangeAssets extends EventEmitter {
    #requestAssets;
    #config;
    #assetsModule;
    #enabledAssetsAtom;
    #fiatBalancesAtom;
    #availableAssetNamesByWalletAccountAtom;
    #exchangeProviders;
    #enabledAssets;
    #exchangeAssetAvailability;
    constructor(params) {
        super();
        this.#assetsModule = params.assetsModule;
        this.#config = params.config;
        this.#enabledAssetsAtom = params.enabledAssetsAtom;
        this.#fiatBalancesAtom = params.fiatBalancesAtom;
        this.#availableAssetNamesByWalletAccountAtom = params.availableAssetNamesByWalletAccountAtom;
        this.#exchangeProviders = params.exchangeProviders;
        this.#enabledAssets = params.enabledAssets;
        this.#exchangeAssetAvailability = params.exchangeAssetAvailability;
        const fetchival = createFetchival({ fetch: params.fetch });
        this.#requestAssets = fetchival(new URL(`${this.#config.services.assets.baseUrl}/assets`));
    }
    searchAssets = async ({ query, walletAccount, direction = SearchDirection.To, networks, }) => {
        const tokens = await this.#memoizedRequestAssets({
            query,
            networks,
        });
        if (tokens.length === 0) {
            this.emit('empty-search', { query, direction, walletAccount, networks });
            return [];
        }
        const [enabledAssetNames, availableAssetNamesByWalletAccount, fiatBalances] = await Promise.all([
            this.#enabledAssetsAtom.get(),
            this.#availableAssetNamesByWalletAccountAtom.get(),
            this.#fiatBalancesAtom.get(),
        ]);
        const availableAssetNamesForWalletAccount = new Set(availableAssetNamesByWalletAccount[walletAccount] || []);
        const enabledAssets = Object.values(this.#assetsModule.getAssets()).filter((asset) => enabledAssetNames[asset.name]);
        const enabledAssetByTicker = new Map(enabledAssets.map((asset) => [asset.ticker, asset]));
        const isBaseAssetSupported = (token) => Boolean(availableAssetNamesForWalletAccount.has(token.network));
        const availableTokens = tokens.filter((token) => isBaseAssetSupported(token));
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const adaptedTokens = availableTokens.map((token) => {
            const walletId = assetAdapter.toWalletId(token.id);
            return {
                ...token,
                id: walletId,
            };
        });
        return adaptedTokens.sort((a, b) => {
            const isAEnabled = enabledAssetByTicker.get(a.id);
            const isBEnabled = enabledAssetByTicker.get(b.id);
            if (isAEnabled && !isBEnabled)
                return -1;
            if (!isAEnabled && isBEnabled)
                return 1;
            if (isAEnabled && isBEnabled) {
                const aAsset = this.getAsset({ assetId: a.id });
                const bAsset = this.getAsset({ assetId: b.id });
                const aFiatBalance = getFiatBalance({
                    assetName: aAsset.name,
                    fiatBalances,
                    walletAccount,
                });
                const bFiatBalance = getFiatBalance({
                    assetName: bAsset.name,
                    fiatBalances,
                    walletAccount,
                });
                return bFiatBalance.sub(aFiatBalance).toDefaultNumber();
            }
            return 0;
        });
    };
    getAsset = memoize((params) => {
        let asset;
        if ('assetName' in params) {
            asset = this.#assetsModule.getAsset(params.assetName);
        }
        else {
            const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
            const walletId = assetAdapter.toWalletId(params.assetId);
            asset = Object.values(this.#assetsModule.getAssets()).find((asset) => asset.ticker === walletId);
        }
        if (!asset) {
            throw new Error(`Unknown asset for ${'assetName' in params ? params.assetName : params.assetId}`);
        }
        return asset;
    }, (params) => ('assetName' in params ? params.assetName : params.assetId), MEMOIZATION_TIMEOUT);
    addAndEnableToken = async (assetId, baseAssetName) => {
        const enabledAssets = await this.#enabledAssetsAtom.get();
        if (!assetId || !enabledAssets[baseAssetName]) {
            await this.#enableBaseAsset(baseAssetName);
            if (!assetId)
                return;
        }
        const assetNames = await this.#assetsModule.addTokens({ assetIds: [assetId], baseAssetName });
        const allEnabledAssets = await this.#enabledAssetsAtom.get();
        const allEnabledAssetIds = this.#getAssetIds([
            ...Object.keys(allEnabledAssets).filter((assetName) => allEnabledAssets[assetName]),
            ...assetNames,
        ]);
        await this.#exchangeAssetAvailability.update(allEnabledAssetIds, this.#exchangeProviders.getSources());
        await this.#enabledAssets.enable(assetNames);
    };
    #memoizedRequestAssets = memoize(async (params) => {
        return this.#requestAssets.get({
            query: params.query,
            ...(params.networks ? { networks: params.networks } : {}),
        });
    }, (params) => `${params.query}-${params.networks}`, MEMOIZATION_TIMEOUT);
    #getAssetIds = (assetNames) => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        return assetNames
            .map((assetName) => {
            const asset = this.#assetsModule.getAsset(assetName);
            if (!asset)
                return null;
            return assetAdapter.toExchangeId(asset.ticker);
        })
            .filter(Boolean);
    };
    #enableBaseAsset = async (baseAssetName) => {
        await this.#enabledAssets.enable([baseAssetName]);
        const allEnabledAssets = await this.#enabledAssetsAtom.get();
        const allEnabledAssetIds = this.#getAssetIds(Object.keys(allEnabledAssets).filter((assetName) => allEnabledAssets[assetName]));
        await this.#exchangeAssetAvailability.update(allEnabledAssetIds, this.#exchangeProviders.getSources());
    };
}
const exchangeAssetsModuleFactory = (args) => new ExchangeAssets({ ...args });
const exchangeAssetsModuleDefinition = {
    id: 'exchangeAssets',
    type: 'module',
    factory: exchangeAssetsModuleFactory,
    dependencies: [
        'config',
        'fetch',
        'assetsModule',
        'enabledAssetsAtom',
        'exchangeProviders',
        'exchangeAssetAvailability',
        'enabledAssets',
        'availableAssetNamesByWalletAccountAtom',
        'fiatBalancesAtom',
    ],
};
export default exchangeAssetsModuleDefinition;
